import { Component, Input, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { KontrollterminGenerationService, Kontrolltermin, GenerationRun, GenerationRunItem } from '../../services/kontrolltermin-generation.service';
import { EditKontrollterminDialogComponent } from './edit-kontrolltermin-dialog/edit-kontrolltermin-dialog.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-kontrolltermin-preview',
  templateUrl: './kontrolltermin-preview.component.html',
  styleUrls: ['./kontrolltermin-preview.component.scss']
})
export class KontrollterminPreviewComponent implements OnInit {
  @Input() standortId: string = '';
  @Input() projektaufgabeId?: string;
  
  kontrolltermine: Kontrolltermin[] = [];
  loading = false;
  error: string | null = null;
  
  // Generation state
  isGenerating = false;
  generationRun: GenerationRun | null = null;
  generationItems: GenerationRunItem[] = [];
  
  // Test run preview
  testRunPreview: any[] = [];
  showTestRunPreview = false;
  
  // Month/Year range picker
  fromMonth: number = new Date().getMonth() + 1; // Current month (1-12)
  fromYear: number = new Date().getFullYear(); // Current year
  toMonth: number = 12; // December
  toYear: number = new Date().getFullYear() + 1; // Next year
  
  // Available months and years
  months = [
    { value: 1, label: 'Januar' },
    { value: 2, label: 'Februar' },
    { value: 3, label: 'März' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Dezember' }
  ];
  
  years: number[] = [];

  constructor(
    private kontrollterminService: KontrollterminGenerationService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initializeYears();
    this.loadKontrolltermine();
  }
  
  private initializeYears(): void {
    const currentYear = new Date().getFullYear();
    this.years = [currentYear, currentYear + 1, currentYear + 2];
  }
  
  private getFromDate(): Date {
    return new Date(this.fromYear, this.fromMonth - 1, 1); // First day of from month
  }
  
  private getToDate(): Date {
    return new Date(this.toYear, this.toMonth, 0); // Last day of to month
  }

  loadKontrolltermine(): void {
    if (!this.standortId) return;
    
    this.loading = true;
    this.error = null;
    
    this.kontrollterminService.getKontrolltermineForStandort(
      this.standortId,
      this.getFromDate().toISOString().split('T')[0],
      this.getToDate().toISOString().split('T')[0]
    ).subscribe({
      next: (response) => {
        this.kontrolltermine = response.data || [];
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Fehler beim Laden der Kontrolltermine';
        this.loading = false;
        console.error('Error loading kontrolltermine:', error);
      }
    });
  }

  generateKontrolltermine(isTestRun: boolean = false): void {
    if (!this.standortId) return;
    
    this.isGenerating = true;
    this.kontrollterminService.generateKontrolltermine({
      standort_id: this.standortId,
      from: this.getFromDate().toISOString().split('T')[0],
      to: this.getToDate().toISOString().split('T')[0],
      projektaufgabe_id: this.projektaufgabeId,
      test_run: isTestRun
    }).subscribe({
      next: (response) => {
        if (isTestRun) {
          // For test run, poll the run details to show what would be generated
          this.pollTestRun(response.run_id);
        } else {
          this.pollGenerationRun(response.run_id);
        }
      },
      error: (error) => {
        this.isGenerating = false;
        this.snackBar.open('Fehler bei der Generierung', 'Schließen');
        console.error('Error generating kontrolltermine:', error);
      }
    });
  }

  private pollTestRun(runId: string): void {
    const pollInterval = setInterval(() => {
      this.kontrollterminService.getRun(runId).subscribe({
        next: (response) => {
          this.generationRun = response.data.run;
          this.generationItems = response.data.items;
          
          if (response.data.run.finished_at) {
            clearInterval(pollInterval);
            this.isGenerating = false;
            
            // Show test run results
            const run = response.data.run;
            if (run.errors_count > 0 && run.notes?.includes('No active projektaufgaben')) {
              this.snackBar.open('Test Run: Keine aktiven Projektaufgaben gefunden', 'Schließen');
              this.showTestRunPreview = false;
            } else {
              // Generate preview data from generation items
              this.generateTestRunPreview();
              this.showTestRunPreview = true;
              const message = `Test Run: ${run.total_candidates} Kontrolltermine würden erstellt werden`;
              this.snackBar.open(message, 'Schließen', { duration: 5000 });
            }
          }
        },
        error: (error) => {
          clearInterval(pollInterval);
          this.isGenerating = false;
          this.snackBar.open('Fehler beim Test Run', 'Schließen');
        }
      });
    }, 1000);
  }

  private generateTestRunPreview(): void {
    this.testRunPreview = [];
    
    // Group generation items by projektaufgabe
    const groupedItems = this.generationItems.reduce((acc, item) => {
      if (!acc[item.projektaufgabe_id]) {
        acc[item.projektaufgabe_id] = {
          projektaufgabe_id: item.projektaufgabe_id,
          dates: []
        };
      }
      acc[item.projektaufgabe_id].dates.push({
        date: item.datum,
        status: this.getActionStatusLabel(item.action || 'created'),
        einsatznummer: this.generatePreviewEinsatznummer(item.projektaufgabe_id, item.datum),
        action: item.action || 'created',
        message: item.message || null
      });
      return acc;
    }, {});

    // Convert to array for display
    Object.values(groupedItems).forEach((group: any) => {
      this.testRunPreview.push({
        projektaufgabe_id: group.projektaufgabe_id,
        kontrolltermine: group.dates.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
      });
    });
  }

  private getActionStatusLabel(action: string): string {
    switch (action) {
      case 'created': return 'GEPLANT';
      case 'skipped': return 'ÜBERSPRUNGEN';
      case 'error': return 'FEHLER';
      default: return 'UNBEKANNT';
    }
  }

  private generatePreviewEinsatznummer(projektaufgabeId: string, date: string): string {
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `KT-${projektaufgabeId.substring(0, 8)}-${year}${month}${day}`;
  }

  private pollGenerationRun(runId: string): void {
    const pollInterval = setInterval(() => {
      this.kontrollterminService.getRun(runId).subscribe({
        next: (response) => {
          this.generationRun = response.data.run;
          this.generationItems = response.data.items;
          
          if (response.data.run.finished_at) {
            clearInterval(pollInterval);
            this.isGenerating = false;
            this.loadKontrolltermine(); // Refresh the list
            
            // Check if no active projektaufgaben were found
            if (response.data.run.errors_count > 0 && response.data.run.notes?.includes('No active projektaufgaben')) {
              this.snackBar.open('Keine aktiven Projektaufgaben gefunden für diesen Standort', 'Schließen');
            } else {
              this.snackBar.open('Generierung abgeschlossen', 'Schließen');
            }
          }
        },
        error: (error) => {
          clearInterval(pollInterval);
          this.isGenerating = false;
          this.snackBar.open('Fehler beim Abrufen des Generierungsstatus', 'Schließen');
        }
      });
    }, 2000); // Poll every 2 seconds
  }

  editKontrolltermin(kontrolltermin: Kontrolltermin): void {
    if (!kontrolltermin.id) {
      this.snackBar.open('Fehler: Kontrolltermin ID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(EditKontrollterminDialogComponent, {
      width: '600px',
      maxHeight: '80vh',
      data: {
        kontrolltermin: kontrolltermin,
        standortId: this.standortId
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Update the kontrolltermin in the local array
        const index = this.kontrolltermine.findIndex(kt => kt.id === result.id);
        if (index !== -1) {
          this.kontrolltermine[index] = result;
          console.log('Updated kontrolltermin in table:', result);
          console.log('Updated techniker_name:', result.techniker_name);
          console.log('Updated status_name:', result.status_name);
        }
        // Success message is already shown in the dialog
      }
    });
  }

  getStatusLabel(statusId: number | string): string {
    // Convert to number if it's a string
    const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    // Use your existing environment configuration
    const status = environment.kontrolltermin_status.find(s => s.id === id);
    return status ? status.label : 'Unbekannt';
  }

  getStatusClass(statusId: number | string): string {
    // Convert to number if it's a string
    const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    const classMap: {[key: number]: string} = {
      1: 'bg-red-100 text-red-800',      // geloescht
      2: 'bg-orange-100 text-orange-800', // abgebrochen
      3: 'bg-red-100 text-red-800',      // fehlerhaft
      4: 'bg-blue-100 text-blue-800',    // geplant
      5: 'bg-yellow-100 text-yellow-800', // aktuell
      6: 'bg-yellow-100 text-yellow-800', // in_bearbeitung
      7: 'bg-purple-100 text-purple-800', // freigabe
      8: 'bg-green-100 text-green-800'   // abgeschlossen
    };
    return classMap[id] || 'bg-gray-100 text-gray-800';
  }

  getPeriodicityLabel(periodizitaet: number): string {
    // Use your existing environment configuration
    const period = (environment as any).periode_aufgaben.find((p: any) => p.id === periodizitaet);
    return period ? period.label : 'Unbekannt';
  }

  onDateRangeChange(): void {
    this.loadKontrolltermine();
  }
  
  getMonthLabel(monthValue: number): string {
    const month = this.months.find(m => m.value === monthValue);
    return month ? month.label : '';
  }

  closeTestRunPreview(): void {
    this.showTestRunPreview = false;
    this.testRunPreview = [];
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  getTotalKontrolltermine(): number {
    return this.testRunPreview.reduce((total, group) => total + group.kontrolltermine.length, 0);
  }

  getCreatedCount(): number {
    return this.testRunPreview.reduce((total, group) => 
      total + group.kontrolltermine.filter((kt: any) => kt.action === 'created').length, 0);
  }

  getSkippedCount(): number {
    return this.testRunPreview.reduce((total, group) => 
      total + group.kontrolltermine.filter((kt: any) => kt.action === 'skipped').length, 0);
  }

  getErrorCount(): number {
    return this.testRunPreview.reduce((total, group) => 
      total + group.kontrolltermine.filter((kt: any) => kt.action === 'error').length, 0);
  }

  getEinsatztypLabel(einsatztyp: number): string {
    // Add einsatztyp mapping logic here
    return 'Kontrolle'; // Default for now
  }
}
